import { MARCADORES_POR_FOLHA, estiloIdsSchema, type IdDeEstiloDeArte } from "@foto/shared";

/**
 * A escolha da direção de arte de um conjunto de marcadores, sem tela.
 *
 * O formulário tem quatro jeitos de dizer como a arte sai, e **só um vale por vez**: o que o
 * servidor recebe sai do modo escolhido, e nunca de um resto de outro modo — quem escolheu três
 * estilos, desistiu e voltou ao Automático não pode mandar os três mesmo assim. Por isso a conta
 * mora aqui, pura e testável, e a tela só mostra o estado e o resultado dela.
 */

/**
 * - `automatico`: nada vai no pedido; o servidor escolhe três estilos e cria o cenário do tema.
 * - `escolher`: três estilos do catálogo, um por marcador (`estiloIds`).
 * - `modelo`: as três direções de um modelo pré-criado (`modeloId`), com ajuste à mão se quiser.
 * - `escrever`: o operador escreve a direção de cada marcador (`estilos`).
 */
export type ModoDeArte = "automatico" | "escolher" | "modelo" | "escrever";

export interface EstadoDaArte {
  modo: ModoDeArte;
  /** O estilo de cada marcador no modo `escolher`; `""` = ainda sem escolha. */
  escolha: string[];
  /** O modelo do modo `modelo`; `""` = nenhum. É texto porque vem de um `<select>`. */
  modeloId: string;
  /** As direções do modelo editadas à mão; nulo = vale o que o modelo traz. */
  ajustadas: string[] | null;
  /** O que o operador escreveu no modo `escrever`, uma direção por marcador. */
  escritas: string[];
}

const umaPorMarcador = (): string[] => Array.from({ length: MARCADORES_POR_FOLHA }, () => "");

export function arteInicial(): EstadoDaArte {
  return {
    modo: "automatico",
    escolha: umaPorMarcador(),
    modeloId: "",
    ajustadas: null,
    escritas: umaPorMarcador(),
  };
}

/** O menor texto de direção que o servidor aceita (`estilosDoMarcadorSchema`). */
export const TAMANHO_MINIMO_DA_DIRECAO = 10;

export const direcaoValida = (texto: string): boolean =>
  texto.trim().length >= TAMANHO_MINIMO_DA_DIRECAO;

/**
 * O marcador (0, 1 ou 2) que já tem este estilo, sem contar `ignorar` — o próprio seletor —; `-1`
 * se o estilo está livre. O estilo vazio nunca está "em uso": é a ausência de escolha.
 */
export function marcadorQueUsa(escolha: readonly string[], id: string, ignorar: number): number {
  if (id === "") return -1;
  return escolha.findIndex((x, i) => i !== ignorar && x === id);
}

/**
 * Escolhe o estilo de um marcador. Repetir o de outro marcador não vale — o conjunto sairia com
 * duas faixas iguais —: a escolha fica como estava. A tela já desabilita essas opções; isto é a
 * garantia de quem não passa por ela. `""` desfaz a escolha.
 */
export function escolherEstilo(escolha: readonly string[], marcador: number, id: string): string[] {
  if (marcadorQueUsa(escolha, id, marcador) !== -1) return [...escolha];
  return escolha.map((x, i) => (i === marcador ? id : x));
}

/** O que `marcadores.gerar` recebe da direção de arte: nenhuma chave, ou só a do modo. */
export interface PedidoDeArte {
  modeloId?: number;
  estilos?: string[];
  estiloIds?: IdDeEstiloDeArte[];
}

export type ResultadoDaArte =
  | { pronta: true; pedido: PedidoDeArte }
  | {
      pronta: false;
      /** O que falta, em português, para quem preenche. */
      falta: string;
      /**
       * Nada foi escolhido ainda. Os campos vazios já dizem o que fazer ("Escolha um estilo"), e a
       * frase de `falta` só os repetiria: ela é para quando a escolha está pela metade ou errada.
       */
      intocada: boolean;
    };

/**
 * O que enviar, ou o que falta para poder enviar.
 *
 * `modelos` é a lista que a tela tem: um `modeloId` que já não está nela (o modelo foi apagado
 * enquanto o formulário estava aberto) conta como "nenhum".
 */
export function pedidoDeArte(
  arte: EstadoDaArte,
  modelos: readonly { id: number }[],
): ResultadoDaArte {
  switch (arte.modo) {
    case "automatico":
      return { pronta: true, pedido: {} };

    case "escolher": {
      // A mesma regra do servidor: três ids do catálogo, diferentes entre si.
      const validos = estiloIdsSchema.safeParse(arte.escolha);
      if (validos.success) return { pronta: true, pedido: { estiloIds: validos.data } };
      const nenhum = arte.escolha.every((x) => x === "");
      return {
        pronta: false,
        intocada: nenhum,
        falta: nenhum
          ? "Escolha o estilo de cada marcador."
          : arte.escolha.some((x) => x === "")
            ? "Escolha o estilo dos marcadores que faltam."
            : "Escolha três estilos diferentes.",
      };
    }

    case "modelo": {
      const modelo = modelos.find((m) => String(m.id) === arte.modeloId);
      if (!modelo) return { pronta: false, falta: "Escolha um modelo.", intocada: true };
      if (arte.ajustadas === null) return { pronta: true, pedido: { modeloId: modelo.id } };
      if (!arte.ajustadas.every(direcaoValida)) {
        return {
          pronta: false,
          intocada: false,
          falta: `Uma das direções ficou curta: escreva pelo menos ${TAMANHO_MINIMO_DA_DIRECAO} letras em cada.`,
        };
      }
      // O texto editado vale mais que o modelo, e o servidor lê `estilos` primeiro; o `modeloId`
      // segue junto porque o modelo continua sendo a origem dos textos de partida.
      return { pronta: true, pedido: { modeloId: modelo.id, estilos: arte.ajustadas } };
    }

    case "escrever":
      if (!arte.escritas.every(direcaoValida)) {
        return {
          pronta: false,
          intocada: false,
          falta: `Escreva a direção de arte dos três marcadores, com pelo menos ${TAMANHO_MINIMO_DA_DIRECAO} letras cada.`,
        };
      }
      return { pronta: true, pedido: { estilos: arte.escritas } };
  }
}

/**
 * Editar o texto de um marcador o desliga do estilo do catálogo: o servidor tira o nome daquela
 * posição quando o texto reenviado difere do guardado (e o mantém se vier idêntico).
 */
export function perdeONomeDoEstilo(nome: string | null, guardado: string, atual: string): boolean {
  return nome !== null && atual.trim() !== guardado.trim();
}
